import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Search } from 'lucide-react';
import { NRC_TABLE_ROWS } from '../../../lib/nrcDecoder/constants';

const CATEGORY_BADGE_CLASS = {
  Reserved: 'text-muted-foreground border-muted-foreground/30 bg-muted/40',
  'Manufacturer Specific': 'text-amber-600 border-amber-600/40 bg-amber-600/10',
};

// Full standardized NRC reference table with client-side search — matches by NRC value
// (hex or decimal), name, category keyword, or description text.
const NrcReferenceTable = ({ onSelectRow }) => {
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NRC_TABLE_ROWS;
    const qHex = q.replace(/^0x/, '');
    return NRC_TABLE_ROWS.filter((r) => r.searchText.includes(q) || (qHex && r.searchText.includes(qHex)));
  }, [query]);

  return (
    <Card className="technical-card" data-testid="nrc-reference-table-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">NRC Reference Table</CardTitle>
        <CardDescription>
          Every standardized ISO 14229-1 negative response code, plus the reserved and vehicle-manufacturer-specific ranges. Search by NRC value, name, or keyword (e.g. &quot;security&quot;, &quot;voltage&quot;, &quot;programming&quot;).
        </CardDescription>
        <div className="relative pt-2">
          <Search className="absolute left-2.5 top-[1.15rem] h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8 font-mono"
            placeholder="Search NRC, name, or keyword..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search NRC reference table"
            data-testid="nrc-table-search-input"
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border border-structure overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">NRC</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Decimal</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Category</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.key}
                  className={onSelectRow && !r.reserved ? 'cursor-pointer' : ''}
                  onClick={() => onSelectRow && !r.reserved && onSelectRow(r)}
                  data-testid={`nrc-table-row-${r.key}`}
                >
                  <TableCell className="font-mono font-semibold whitespace-nowrap">{r.hex}</TableCell>
                  <TableCell className="font-mono text-xs whitespace-nowrap text-muted-foreground">{r.decimal}</TableCell>
                  <TableCell className="text-sm">{r.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`font-mono text-[10px] font-normal ${CATEGORY_BADGE_CLASS[r.category] || ''}`}>
                      {r.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md">{r.description}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                    No NRCs match &quot;{query}&quot;.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default NrcReferenceTable;
